import { createSlice } from '@reduxjs/toolkit';
import {
  BASE_END_POINT_FILE,
  BASE_END_POINT_JOB,
  FILE_END_POINT,
  GET_USER_FILE_END_POINT,
  JOB_END_POINT
} from '../../../config';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';

const initialState = {
  companyApplications: [], // Applications for jobs owned by the company
  candidateApplications: [], // Applications submitted by the candidate
  appliedByJobId: {},
  checkStatusByJobId: {},
  submitStatusByJobId: {},
  submitErrorByJobId: {},
  loading: false,
  error: null,
  fetched: false,
  candidateFetched: false,
  updating: false, // Track if we're updating an application status
  selectedApplication: null,
  selectedApplicationLoading: false,
  selectedApplicationError: null,
  scorecardForm: {
    rating: 'very_good',
    feedbackText: ''
  },
  scorecardSaving: false,
  scorecardError: null,
  scorecardSaved: false
};

const applicationsSlice = createSlice({
  name: 'applications',
  initialState,
  reducers: {
    fetchApplicationsRequest(state) {
      state.loading = true;
      state.error = null;
    },
    fetchApplicationsSuccess(state, action) {
      state.loading = false;
      state.companyApplications = action.payload.applications;
      state.fetched = true;
    },
    fetchApplicationsFailure(state, action) {
      state.loading = false;
      state.error = action.payload.error;
    },
    clearApplications(state) {
      state.companyApplications = [];
      state.candidateApplications = [];
      state.appliedByJobId = {};
      state.checkStatusByJobId = {};
      state.submitStatusByJobId = {};
      state.submitErrorByJobId = {};
      state.fetched = false;
      state.candidateFetched = false;
      state.error = null;
    },
    checkApplicationRequest(state, action) {
      const { jobId } = action.payload;
      state.checkStatusByJobId[jobId] = 'loading';
    },
    checkApplicationSuccess(state, action) {
      const { jobId, applied } = action.payload;
      state.checkStatusByJobId[jobId] = 'succeeded';
      state.appliedByJobId[jobId] = applied;
    },
    checkApplicationFailure(state, action) {
      const { jobId, error } = action.payload;
      state.checkStatusByJobId[jobId] = 'failed';
      state.appliedByJobId[jobId] = false;
      state.error = error;
    },
    submitApplicationRequest(state, action) {
      const { jobId } = action.payload;
      state.submitStatusByJobId[jobId] = 'loading';
      state.submitErrorByJobId[jobId] = null;
    },
    submitApplicationSuccess(state, action) {
      const { jobId, application } = action.payload;
      state.submitStatusByJobId[jobId] = 'succeeded';
      state.appliedByJobId[jobId] = true;
      if (application) {
        state.candidateApplications = [
          application,
          ...state.candidateApplications
        ];
      }
    },
    submitApplicationFailure(state, action) {
      const { jobId, error } = action.payload;
      state.submitStatusByJobId[jobId] = 'failed';
      state.submitErrorByJobId[jobId] = error;
      state.error = error;
    },
    resetSubmitApplicationStatus(state, action) {
      const { jobId } = action.payload;
      state.submitStatusByJobId[jobId] = 'idle';
      state.submitErrorByJobId[jobId] = null;
    },
    updateApplicationStatusRequest(state) {
      state.updating = true;
    },
    updateApplicationStatusSuccess(state, action) {
      state.updating = false;
      // Update the application in the list
      const { applicationId, newStatus } = action.payload;
      const appIndex = state.companyApplications.findIndex(app => app.id === applicationId);
      if (appIndex !== -1) {
        state.companyApplications[appIndex].status = newStatus;
        state.companyApplications[appIndex].updatedAt = Date.now();
      }
    },
    updateApplicationStatusFailure(state, action) {
      state.updating = false;
      state.error = action.payload.error;
    },
    fetchApplicationByIdRequest(state) {
      state.selectedApplicationLoading = true;
      state.selectedApplicationError = null;
    },
    fetchApplicationByIdSuccess(state, action) {
      state.selectedApplicationLoading = false;
      state.selectedApplication = action.payload.application;
      state.selectedApplicationError = null;
      const scorecard =
        action.payload.application?.scorecards?.[0] ||
        action.payload.application?.scorecard;
      state.scorecardForm = {
        rating: scorecard?.rating || 'very_good',
        feedbackText: scorecard?.feedbackText || ''
      };
    },
    fetchApplicationByIdFailure(state, action) {
      state.selectedApplicationLoading = false;
      state.selectedApplicationError = action.payload.error;
    },
    updateScorecardForm(state, action) {
      state.scorecardForm = {
        ...state.scorecardForm,
        ...action.payload
      };
    },
    resetScorecardForm(state, action) {
      const scorecard = action.payload?.scorecard;
      state.scorecardForm = {
        rating: scorecard?.rating || 'very_good',
        feedbackText: scorecard?.feedbackText || ''
      };
      state.scorecardError = null;
    },
    saveScorecardRequest(state) {
      state.scorecardSaving = true;
      state.scorecardError = null;
      state.scorecardSaved = false;
    },
    saveScorecardSuccess(state, action) {
      state.scorecardSaving = false;
      state.scorecardError = null;
      state.scorecardSaved = true;
      if (state.selectedApplication) {
        const existingScorecards = state.selectedApplication.scorecards || [];
        state.selectedApplication = {
          ...state.selectedApplication,
          scorecard: action.payload.scorecard,
          scorecards: [action.payload.scorecard, ...existingScorecards]
        };
      }
    },
    saveScorecardFailure(state, action) {
      state.scorecardSaving = false;
      state.scorecardError = action.payload.error;
      state.scorecardSaved = false;
    },
    resetScorecardSaved(state) {
      state.scorecardSaved = false;
    },
    fetchCandidateApplicationsRequest(state) {
      state.loading = true;
      state.error = null;
    },
    fetchCandidateApplicationsSuccess(state, action) {
      state.loading = false;
      state.candidateApplications = action.payload.applications;
      state.candidateFetched = true;
    },
    fetchCandidateApplicationsFailure(state, action) {
      state.loading = false;
      state.error = action.payload.error;
    }
  }
});

export const {
  fetchApplicationsRequest,
  fetchApplicationsSuccess,
  fetchApplicationsFailure,
  clearApplications,
  updateApplicationStatusRequest,
  updateApplicationStatusSuccess,
  updateApplicationStatusFailure,
  fetchApplicationByIdRequest,
  fetchApplicationByIdSuccess,
  fetchApplicationByIdFailure,
  updateScorecardForm,
  resetScorecardForm,
  saveScorecardRequest,
  saveScorecardSuccess,
  saveScorecardFailure,
  resetScorecardSaved,
  fetchCandidateApplicationsRequest,
  fetchCandidateApplicationsSuccess,
  fetchCandidateApplicationsFailure,
  checkApplicationRequest,
  checkApplicationSuccess,
  checkApplicationFailure,
  submitApplicationRequest,
  submitApplicationSuccess,
  submitApplicationFailure,
  resetSubmitApplicationStatus
} = applicationsSlice.actions;

// Selectors
export const applicationsSelector = (state) => state.applications.companyApplications;
export const candidateApplicationsSelector = (state) => state.applications.candidateApplications;
export const applicationsLoadingSelector = (state) => state.applications.loading;
export const applicationsErrorSelector = (state) => state.applications.error;
export const applicationsFetchedSelector = (state) => state.applications.fetched;
export const candidateApplicationsFetchedSelector = (state) => state.applications.candidateFetched;
export const applicationsUpdatingSelector = (state) => state.applications.updating;
export const selectedApplicationSelector = (state) => state.applications.selectedApplication;
export const selectedApplicationLoadingSelector = (state) =>
  state.applications.selectedApplicationLoading;
export const selectedApplicationErrorSelector = (state) =>
  state.applications.selectedApplicationError;
export const scorecardFormSelector = (state) => state.applications.scorecardForm;
export const scorecardSavingSelector = (state) => state.applications.scorecardSaving;
export const scorecardErrorSelector = (state) => state.applications.scorecardError;
export const scorecardSavedSelector = (state) => state.applications.scorecardSaved;
export const applicationAppliedSelector = (jobId) => (state) =>
  Boolean(state.applications.appliedByJobId[jobId]);
export const applicationCheckStatusSelector = (jobId) => (state) =>
  state.applications.checkStatusByJobId[jobId] || 'idle';
export const applicationSubmitStatusSelector = (jobId) => (state) =>
  state.applications.submitStatusByJobId[jobId] || 'idle';
export const applicationSubmitErrorSelector = (jobId) => (state) =>
  state.applications.submitErrorByJobId[jobId] || null;

// Thunk to fetch all applications for company's jobs
export const fetchCompanyApplications = () => async (dispatch) => {
  console.log('📋 [Applications Slice] Fetching company applications');
  dispatch(fetchApplicationsRequest());

  try {
    const localAuth = getLocalStorageAuthentication();
    
    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    // First, get all jobs for the current user
    const jobsResponse = await fetch(
      `${BASE_END_POINT_JOB}${JOB_END_POINT}/user/${localAuth.userId}`,
      {
        headers: {
          Authorization: `Bearer ${localAuth.token}`
        }
      }
    );

    if (!jobsResponse.ok) {
      throw new Error('Failed to fetch jobs');
    }

    const jobsData = await jobsResponse.json();
    const jobResults = jobsData.data || [];
    
    // Extract jobs from JobResult objects (API returns { job, company, file })
    const jobs = jobResults
      .map((jobResult) => jobResult.job)
      .filter((job) => job && job.id);

    console.log(`📋 [Applications Slice] Found ${jobs.length} jobs for company`);

    // Fetch applications for each job
    const applicationsPromises = jobs.map(async (job) => {
      try {
        const url = `${BASE_END_POINT_JOB}/api/v1/application/job/${job.id}`;
        console.log(`📡 [Applications Slice] Fetching applications from: ${url}`);
        
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          }
        });

        console.log(`📡 [Applications Slice] Response status for job ${job.id}: ${response.status}`);

        if (!response.ok) {
          const errorText = await response.text();
          console.warn(`⚠️ Failed to fetch applications for job ${job.id}. Status: ${response.status}, Error: ${errorText}`);
          return [];
        }

        const data = await response.json();
        console.log(`📦 [Applications Slice] Raw response data for job ${job.id}:`, data);
        
        const applications = data.data || [];
        console.log(`📋 [Applications Slice] Found ${applications.length} applications for job "${job.title}" (${job.id})`);

        // Enrich applications with job info
        return applications.map(app => ({
          ...app,
          jobTitle: job.title,
          jobId: job.id
        }));
      } catch (error) {
        console.error(`❌ Error fetching applications for job ${job.id}:`, error);
        return [];
      }
    });

    const applicationsArrays = await Promise.all(applicationsPromises);
    const allApplications = applicationsArrays.flat();

    console.log(`✅ [Applications Slice] Fetched ${allApplications.length} total applications`);

    dispatch(
      fetchApplicationsSuccess({
        applications: allApplications
      })
    );

    return { success: true, applications: allApplications };
  } catch (error) {
    console.error('❌ [Applications Slice] Error:', error);

    dispatch(
      fetchApplicationsFailure({
        error: error.message
      })
    );

    dispatch(
      messageUpdated({
        text: 'Failed to load applications',
        severity: Severity.ERROR
      })
    );

    return { success: false, error: error.message };
  }
};

export const fetchApplicationById = (applicationId) => async (dispatch) => {
  dispatch(fetchApplicationByIdRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `${BASE_END_POINT_JOB}/api/v1/application/${applicationId}`,
      {
        headers: {
          Authorization: `Bearer ${localAuth.token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to load application');
    }

    const data = await response.json();
    const application = data.data || data;

    let candidateAvatarUrl = application?.candidateAvatarUrl || '';
    const candidateUserId = application?.userId;

    if (candidateUserId) {
      try {
        const fileResponse = await fetch(
          `${BASE_END_POINT_FILE}${FILE_END_POINT}${GET_USER_FILE_END_POINT}/${encodeURIComponent(
            candidateUserId
          )}`,
          {
            headers: {
              Authorization: `Bearer ${localAuth.token}`
            }
          }
        );

        if (fileResponse.ok) {
          const fileData = await fileResponse.json();
          const resolvedFile =
            Array.isArray(fileData?.data) && fileData.data.length > 0
              ? fileData.data[0]
              : fileData?.data;
          candidateAvatarUrl = resolvedFile?.publicUrl || '';
        }
      } catch (fileError) {
        console.warn('⚠️ [Applications Slice] Failed to load candidate avatar', fileError);
      }
    }

    dispatch(
      fetchApplicationByIdSuccess({
        application: {
          ...application,
          candidateAvatarUrl
        }
      })
    );

    return { success: true, application };
  } catch (error) {
    dispatch(
      fetchApplicationByIdFailure({
        error: error.message
      })
    );
    return { success: false, error };
  }
};

export const saveApplicationScorecard = (applicationId) => async (dispatch, getState) => {
  dispatch(saveScorecardRequest());

  try {
    const localAuth = getLocalStorageAuthentication();
    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const { rating, feedbackText } = getState().applications.scorecardForm;

    const response = await fetch(
      `${BASE_END_POINT_JOB}/api/v1/application/${applicationId}/scorecard`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localAuth.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rating, feedbackText })
      }
    );

    if (!response.ok) {
      throw new Error('Failed to save scorecard');
    }

    const data = await response.json();
    const scorecard = data.data || data;

    dispatch(
      saveScorecardSuccess({
        scorecard
      })
    );

    return { success: true, scorecard };
  } catch (error) {
    dispatch(
      saveScorecardFailure({
        error: error.message
      })
    );
    dispatch(
      messageUpdated({
        text: error.message || 'Failed to save scorecard',
        severity: Severity.ERROR
      })
    );
    return { success: false, error };
  }
};

// Thunk to update application status
export const updateApplicationStatus = (applicationId, newStatus) => async (dispatch) => {
  console.log(`🔄 [Applications Slice] Updating application ${applicationId} to status: ${newStatus}`);
  dispatch(updateApplicationStatusRequest());

  try {
    const localAuth = getLocalStorageAuthentication();
    
    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `${BASE_END_POINT_JOB}/api/v1/application/${applicationId}/status`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify({ status: newStatus })
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorMsg = `Failed to update status`;
      try {
        const errorData = JSON.parse(errorText);
        errorMsg = errorData?.message || errorMsg;
      } catch (e) {
        // Not JSON
      }
      throw new Error(errorMsg);
    }

    const data = await response.json();
    console.log('✅ [Applications Slice] Status updated successfully:', data);

    dispatch(
      updateApplicationStatusSuccess({
        applicationId,
        newStatus
      })
    );

    dispatch(
      messageUpdated({
        text: 'Application status updated successfully',
        severity: Severity.SUCCESS
      })
    );

    return { success: true };
  } catch (error) {
    console.error('❌ [Applications Slice] Failed to update status:', error);

    dispatch(
      updateApplicationStatusFailure({
        error: error.message
      })
    );

    dispatch(
      messageUpdated({
        text: error.message || 'Failed to update application status',
        severity: Severity.ERROR
      })
    );

    return { success: false, error: error.message };
  }
};

// Thunk to fetch applications for the current candidate user
export const fetchCandidateApplications = () => async (dispatch) => {
  console.log('📋 [Applications Slice] Fetching candidate applications');
  dispatch(fetchCandidateApplicationsRequest());

  try {
    const localAuth = getLocalStorageAuthentication();

    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/application/my-applications`;
    console.log(`📡 [Applications Slice] Fetching from: ${url}`);

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    console.log(`📡 [Applications Slice] Response status: ${response.status}`);

    if (!response.ok) {
      // Handle 403 gracefully - user might not have permissions yet (e.g., first login after activation)
      if (response.status === 403) {
        console.warn('⚠️ [Applications Slice] 403 Forbidden - User might not have permissions yet. Treating as empty applications list.');
        dispatch(
          fetchCandidateApplicationsSuccess({
            applications: []
          })
        );
        return { success: true, applications: [], warning: 'permissions_pending' };
      }

      const errorText = await response.text();
      throw new Error(`Failed to fetch applications: ${errorText}`);
    }

    const data = await response.json();
    console.log(`📦 [Applications Slice] Raw response data:`, data);

    const applications = data.data || [];
    console.log(`✅ [Applications Slice] Fetched ${applications.length} candidate applications`);

    dispatch(
      fetchCandidateApplicationsSuccess({
        applications
      })
    );

    return { success: true, applications };
  } catch (error) {
    console.error('❌ [Applications Slice] Error:', error);

    dispatch(
      messageUpdated({
        text: error.message || 'Failed to load your applications',
        severity: Severity.ERROR
      })
    );
    dispatch(
      fetchCandidateApplicationsFailure({
        error: error.message
      })
    );

    return { success: false, error: error.message };
  }
};

export const checkCandidateApplication = (jobId) => async (dispatch) => {
  if (!jobId) {
    return { success: false, error: 'Missing jobId' };
  }

  dispatch(checkApplicationRequest({ jobId }));

  try {
    const localAuth = getLocalStorageAuthentication();
    if (!localAuth || !localAuth.token) {
      throw new Error('Not authenticated');
    }

    const url = `${BASE_END_POINT_JOB}/api/v1/application/check/${jobId}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      dispatch(checkApplicationSuccess({ jobId, applied: false }));
      return { success: false, error: `Check failed: ${response.status}` };
    }

    const text = await response.text();
    if (!text) {
      dispatch(checkApplicationSuccess({ jobId, applied: false }));
      return { success: true, applied: false };
    }

    const data = JSON.parse(text);
    const applied = data?.data === true;

    dispatch(checkApplicationSuccess({ jobId, applied }));
    return { success: true, applied };
  } catch (error) {
    dispatch(checkApplicationFailure({ jobId, error: error.message }));
    return { success: false, error: error.message };
  }
};

export const submitCandidateApplication =
  ({ jobId, coverLetter = '' }) =>
  async (dispatch, getState) => {
    if (!jobId) {
      return { success: false, error: 'Missing jobId' };
    }

    dispatch(submitApplicationRequest({ jobId }));

    try {
      const localAuth = getLocalStorageAuthentication();
      if (!localAuth || !localAuth.token) {
        throw new Error('Not authenticated');
      }

      const state = getState();
      const user = state.users?.user;
      const response = await fetch(`${BASE_END_POINT_JOB}/api/v1/application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localAuth.token}`
        },
        body: JSON.stringify({
          jobId,
          candidateName: user?.fullName || 'Unknown Candidate',
          candidateEmail: user?.email || '',
          coverLetter
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMsg = errorData?.message || errorMsg;
        } catch (e) {
          errorMsg = errorText || errorMsg;
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();
      const apiStatus = data?.status;
      if (
        apiStatus &&
        !['success', 'ok'].includes(apiStatus.toLowerCase())
      ) {
        throw new Error(data?.message || 'Failed to submit application');
      }

      dispatch(
        submitApplicationSuccess({
          jobId,
          application: data?.data || null
        })
      );
      dispatch(
        messageUpdated({
          text: 'Application submitted successfully!',
          severity: Severity.SUCCESS
        })
      );

      return { success: true, data: data?.data };
    } catch (error) {
      dispatch(
        submitApplicationFailure({
          jobId,
          error: error.message
        })
      );
      dispatch(
        messageUpdated({
          text: error.message || 'Failed to submit application',
          severity: Severity.ERROR
        })
      );
      return { success: false, error: error.message };
    }
  };

export default applicationsSlice.reducer;
